import { useEffect, type MouseEvent } from 'react'
import { useNotes, useNote } from '../api'
import { useLang } from '../lang'
import { shortDate } from '../time'
import type { NoteSummary } from '../types'
import { Kicker, QuietButton, StateLine } from '../design'
import * as n from './notes.css'

// Notes from the station — the keeper's own writing, kept apart from the daily Journal because
// it is written rather than composed. Two views on one tab: the feed, and one note.
//
// The index is a FEED, not a contents page: notes run newest-first and are read straight down. A
// short one is there whole; a long one shows its opening and "continue reading". Every title is a
// real link to the note's own address — what you open to read a long one, and what you copy to
// cite any one of them alone.
//
// The writing itself lives on a hosted blog; the server reads its feed (NotesFeed) and this sets
// it in the house style. Each note says what language it is written in (`lang`) and its prose is
// marked with that — the chrome around it still follows the reader.
export function NotesTab({
  slug,
  onSlug,
  onTitle,
}: {
  slug: string | null
  onSlug: (slug: string | null) => void
  onTitle: (title: string | null) => void
}) {
  const { lang, t } = useLang()
  const list = useNotes()
  const one = useNote(slug)

  // The open note's title goes up to App, which owns document.title. Cleared on the index and
  // while a note is loading, so the tab never carries the previous note's name.
  const openTitle = slug ? (one.data?.title ?? null) : null
  useEffect(() => {
    onTitle(openTitle)
    return () => onTitle(null)
  }, [openTitle, onTitle])

  if (slug) {
    if (one.isLoading || !one.data) {
      return (
        <section className={n.section}>
          <StateLine busy={!one.isError}>
            {one.isError ? t('That note could not be found.', 'Níorbh fhéidir an nóta sin a aimsiú.') : '…'}
          </StateLine>
        </section>
      )
    }
    const note = one.data
    return (
      <section className={n.section}>
        <article className={n.piece}>
          <div className={n.back}>
            <QuietButton onClick={() => onSlug(null)}>{t('← All notes', '← Gach nóta')}</QuietButton>
          </div>
          <header className={n.pieceHead}>
            <h1 className={n.title} lang={note.lang}>
              {note.title}
            </h1>
            {note.published_at && <p className={n.stamp}>{shortDate(note.published_at, lang)}</p>}
            {note.summary && (
              <p className={n.standfirst} lang={note.lang}>
                {note.summary}
              </p>
            )}
          </header>
          {note.image && <img className={n.lead} src={note.image} alt="" />}
          {/* The one place the SPA sets markup it did not build. NotesFeed has already reduced it
              to a prose safelist server-side — no script, style, iframe or handlers survive. */}
          <div className={n.body} lang={note.lang} dangerouslySetInnerHTML={{ __html: note.html }} />
        </article>
      </section>
    )
  }

  if (list.isLoading || !list.data) {
    return (
      <section className={n.section}>
        <StateLine busy={!list.isError}>{list.isError ? '—' : '…'}</StateLine>
      </section>
    )
  }

  const notes: NoteSummary[] = list.data.notes
  return (
    <section className={n.section}>
      <header className={n.head}>
        <Kicker as="h1">{t('Notes from the station', 'Nótaí ón stáisiún')}</Kicker>
      </header>
      {notes.length === 0 ? (
        <StateLine>{t('Nothing written yet.', 'Níl aon rud scríofa fós.')}</StateLine>
      ) : (
        <div className={n.feed}>
          {notes.map((note) => {
            // A real href, so the address can be copied, opened in a new tab or cited; a plain
            // click stays inside the SPA. Modified clicks are left to the browser.
            const href = `/notes/${note.slug}`
            const open = (e: MouseEvent<HTMLAnchorElement>) => {
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
              e.preventDefault()
              onSlug(note.slug)
              window.scrollTo({ top: 0 })
            }
            return (
              <article key={note.slug} className={n.post}>
                <header className={n.postHead}>
                  {note.published_at && <p className={n.stamp}>{shortDate(note.published_at, lang)}</p>}
                  <h2 className={n.postTitle} lang={note.lang}>
                    <a className={n.postLink} href={href} onClick={open}>
                      {note.title}
                    </a>
                  </h2>
                  {note.summary && (
                    <p className={n.standfirst} lang={note.lang}>
                      {note.summary}
                    </p>
                  )}
                </header>
                {note.image && <img className={n.lead} src={note.image} alt="" loading="lazy" />}
                {/* Sanitised server-side by NotesFeed, exactly as the single note's body is. */}
                <div className={n.body} lang={note.lang} dangerouslySetInnerHTML={{ __html: note.html }} />
                {note.more && (
                  <p className={n.onward}>
                    <a className={n.onwardLink} href={href} onClick={open}>
                      {t('Continue reading →', 'Lean ort ag léamh →')}
                    </a>
                  </p>
                )}
              </article>
            )
          })}
        </div>
      )}
    </section>
  )
}
